/**
 * Text shown on the interviewee's device, in the interviewee's language.
 * The interviewee screen deliberately carries almost no words: the consent
 * screen, a waiting line, and status words. Keep it that way.
 */

export interface IntervieweeStrings {
  welcome: string;
  chooseLanguage: string;
  consentTitle: string;
  consentAccept: string;
  consentDecline: string;
  consentDeclined: string;
  waiting: string;
  listening: string;
  yourTurnHint: string;
  ended: string;
  thanks: string;
  reconnecting: string;
  paused: string;
  tapToEnableSound: string;
  codeExpired: string;
  micBlocked: string;
}

const STRINGS: Record<string, IntervieweeStrings> = {
  en: {
    welcome: "Welcome",
    chooseLanguage: "Choose your language",
    consentTitle: "Before we begin",
    consentAccept: "I agree",
    consentDecline: "I do not agree",
    consentDeclined: "Thank you. Nothing has been recorded.",
    waiting: "The conversation will begin shortly",
    listening: "Listening",
    yourTurnHint: "Speak naturally. Your words will be translated.",
    ended: "The conversation has ended",
    thanks: "Thank you for your time.",
    reconnecting: "Reconnecting…",
    paused: "Paused",
    tapToEnableSound: "Tap to turn on sound",
    codeExpired: "This code has expired or is in use on another phone. Ask the interviewer for a new code.",
    micBlocked: "The microphone is blocked. Allow it in your browser settings, then tap again.",
  },
  es: {
    welcome: "Bienvenido",
    chooseLanguage: "Elija su idioma",
    consentTitle: "Antes de comenzar",
    consentAccept: "Acepto",
    consentDecline: "No acepto",
    consentDeclined: "Gracias. No se ha grabado nada.",
    waiting: "La conversación comenzará en un momento",
    listening: "Escuchando",
    yourTurnHint: "Hable con naturalidad. Sus palabras serán traducidas.",
    ended: "La conversación ha terminado",
    thanks: "Gracias por su tiempo.",
    reconnecting: "Reconectando…",
    paused: "En pausa",
    tapToEnableSound: "Toque para activar el sonido",
    codeExpired: "Este código ha caducado o se está usando en otro teléfono. Pida un código nuevo a la persona entrevistadora.",
    micBlocked: "El micrófono está bloqueado. Permítalo en la configuración del navegador y vuelva a tocar.",
  },
  hi: {
    welcome: "स्वागत है",
    chooseLanguage: "अपनी भाषा चुनें",
    consentTitle: "शुरू करने से पहले",
    consentAccept: "मैं सहमत हूँ",
    consentDecline: "मैं सहमत नहीं हूँ",
    consentDeclined: "धन्यवाद। कुछ भी रिकॉर्ड नहीं किया गया है।",
    waiting: "बातचीत जल्द ही शुरू होगी",
    listening: "सुन रहे हैं",
    yourTurnHint: "सहज रूप से बोलें। आपके शब्दों का अनुवाद किया जाएगा।",
    ended: "बातचीत समाप्त हो गई है",
    thanks: "आपके समय के लिए धन्यवाद।",
    reconnecting: "फिर से जुड़ रहे हैं…",
    paused: "रुका हुआ",
    tapToEnableSound: "आवाज़ चालू करने के लिए टैप करें",
    codeExpired: "यह कोड समाप्त हो गया है या किसी दूसरे फ़ोन पर उपयोग में है। साक्षात्कारकर्ता से नया कोड माँगें।",
    micBlocked: "माइक्रोफ़ोन अवरुद्ध है। ब्राउज़र सेटिंग में अनुमति दें, फिर दोबारा टैप करें।",
  },
  mr: {
    welcome: "स्वागत आहे",
    chooseLanguage: "तुमची भाषा निवडा",
    consentTitle: "सुरू करण्यापूर्वी",
    consentAccept: "मी सहमत आहे",
    consentDecline: "मी सहमत नाही",
    consentDeclined: "धन्यवाद. काहीही रेकॉर्ड केले गेले नाही.",
    waiting: "संभाषण लवकरच सुरू होईल",
    listening: "ऐकत आहोत",
    yourTurnHint: "सहजपणे बोला. तुमच्या शब्दांचे भाषांतर केले जाईल.",
    ended: "संभाषण संपले आहे",
    thanks: "तुमच्या वेळेबद्दल धन्यवाद.",
    reconnecting: "पुन्हा जोडत आहोत…",
    paused: "थांबवले",
    tapToEnableSound: "आवाज सुरू करण्यासाठी टॅप करा",
    codeExpired: "हा कोड कालबाह्य झाला आहे किंवा दुसऱ्या फोनवर वापरात आहे. मुलाखतकर्त्याकडून नवीन कोड मागा.",
    micBlocked: "मायक्रोफोन अवरोधित आहे. ब्राउझर सेटिंगमध्ये परवानगी द्या आणि पुन्हा टॅप करा.",
  },
  pt: {
    welcome: "Bem-vindo",
    chooseLanguage: "Escolha o seu idioma",
    consentTitle: "Antes de começar",
    consentAccept: "Concordo",
    consentDecline: "Não concordo",
    consentDeclined: "Obrigado. Nada foi gravado.",
    waiting: "A conversa começará em breve",
    listening: "Ouvindo",
    yourTurnHint: "Fale naturalmente. Suas palavras serão traduzidas.",
    ended: "A conversa terminou",
    thanks: "Obrigado pelo seu tempo.",
    reconnecting: "Reconectando…",
    paused: "Em pausa",
    tapToEnableSound: "Toque para ativar o som",
    codeExpired: "Este código expirou ou está em uso em outro telefone. Peça um novo código ao entrevistador.",
    micBlocked: "O microfone está bloqueado. Permita-o nas configurações do navegador e toque novamente.",
  },
  fr: {
    welcome: "Bienvenue",
    chooseLanguage: "Choisissez votre langue",
    consentTitle: "Avant de commencer",
    consentAccept: "J'accepte",
    consentDecline: "Je n'accepte pas",
    consentDeclined: "Merci. Rien n'a été enregistré.",
    waiting: "La conversation va bientôt commencer",
    listening: "À l'écoute",
    yourTurnHint: "Parlez naturellement. Vos paroles seront traduites.",
    ended: "La conversation est terminée",
    thanks: "Merci pour votre temps.",
    reconnecting: "Reconnexion…",
    paused: "En pause",
    tapToEnableSound: "Touchez pour activer le son",
    codeExpired: "Ce code a expiré ou est utilisé sur un autre téléphone. Demandez un nouveau code à l'enquêteur.",
    micBlocked: "Le microphone est bloqué. Autorisez-le dans les réglages du navigateur, puis touchez à nouveau.",
  },
};

export function intervieweeStrings(base: string): IntervieweeStrings {
  return STRINGS[base] ?? STRINGS.en!;
}

/**
 * Consent text. `{controller}` is the organisation responsible for the data
 * (the client, with Gramin Kriti as processor). Country addenda carry the
 * local legal reference; local counsel must approve the final wording per
 * engagement before field use (see docs/security/privacy.md).
 */
const CONSENT: Record<string, string> = {
  en:
    "This conversation is part of a review of how work is done at {controller}. With your agreement, it will be recorded, transcribed and translated so we understand your experience accurately.\n\n" +
    "• Your name will not appear in any report. Your words may be quoted without identifying you.\n" +
    "• You can stop at any time, or skip any question.\n" +
    "• Recordings are stored securely and used only for this review.",
  es:
    "Esta conversación forma parte de una revisión de cómo se trabaja en {controller}. Con su autorización, se grabará, transcribirá y traducirá para entender su experiencia con precisión.\n\n" +
    "• Su nombre no aparecerá en ningún informe. Sus palabras podrán citarse sin identificarle.\n" +
    "• Puede detenerse en cualquier momento u omitir cualquier pregunta.\n" +
    "• Las grabaciones se guardan de forma segura y se usan solo para esta revisión.",
  hi:
    "यह बातचीत {controller} में काम कैसे होता है, इसकी समीक्षा का हिस्सा है। आपकी सहमति से इसे रिकॉर्ड, लिखित और अनुवादित किया जाएगा ताकि हम आपके अनुभव को सही ढंग से समझ सकें।\n\n" +
    "• किसी भी रिपोर्ट में आपका नाम नहीं होगा। आपकी पहचान बताए बिना आपके शब्द उद्धृत किए जा सकते हैं।\n" +
    "• आप कभी भी रुक सकते हैं या कोई भी प्रश्न छोड़ सकते हैं।\n" +
    "• रिकॉर्डिंग सुरक्षित रूप से रखी जाती हैं और केवल इस समीक्षा के लिए उपयोग की जाती हैं।",
  mr:
    "हे संभाषण {controller} मध्ये काम कसे होते याच्या आढाव्याचा भाग आहे. तुमच्या संमतीने ते रेकॉर्ड, लिखित आणि भाषांतरित केले जाईल जेणेकरून आम्ही तुमचा अनुभव अचूकपणे समजू शकू.\n\n" +
    "• कोणत्याही अहवालात तुमचे नाव येणार नाही. तुमची ओळख न सांगता तुमचे शब्द उद्धृत केले जाऊ शकतात.\n" +
    "• तुम्ही कधीही थांबू शकता किंवा कोणताही प्रश्न वगळू शकता.\n" +
    "• रेकॉर्डिंग सुरक्षितपणे ठेवल्या जातात आणि फक्त या आढाव्यासाठी वापरल्या जातात.",
  pt:
    "Esta conversa faz parte de uma análise de como o trabalho é feito em {controller}. Com a sua autorização, será gravada, transcrita e traduzida para entendermos a sua experiência com precisão.\n\n" +
    "• Seu nome não aparecerá em nenhum relatório. Suas palavras poderão ser citadas sem identificá-lo.\n" +
    "• Você pode parar a qualquer momento ou pular qualquer pergunta.\n" +
    "• As gravações são armazenadas com segurança e usadas apenas para esta análise.",
  fr:
    "Cette conversation fait partie d'une revue de la façon dont le travail est réalisé chez {controller}. Avec votre accord, elle sera enregistrée, transcrite et traduite afin de bien comprendre votre expérience.\n\n" +
    "• Votre nom n'apparaîtra dans aucun rapport. Vos propos pourront être cités sans vous identifier.\n" +
    "• Vous pouvez arrêter à tout moment ou passer une question.\n" +
    "• Les enregistrements sont conservés de manière sécurisée et utilisés uniquement pour cette revue.",
};

const COUNTRY_ADDENDA: Record<string, Record<string, string>> = {
  MX: {
    es: "\n\nSus datos personales se tratarán conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares. El aviso de privacidad completo está disponible a solicitud.",
    en: "\n\nYour personal data will be handled under Mexico's Federal Law on the Protection of Personal Data Held by Private Parties. The full privacy notice is available on request.",
  },
  IN: {
    en: "\n\nYour personal data will be handled under India's Digital Personal Data Protection Act, 2023.",
    hi: "\n\nआपके व्यक्तिगत डेटा को भारत के डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, 2023 के अनुसार संभाला जाएगा।",
    mr: "\n\nतुमचा वैयक्तिक डेटा भारताच्या डिजिटल वैयक्तिक डेटा संरक्षण अधिनियम, 2023 नुसार हाताळला जाईल.",
  },
};

export const CONSENT_TEXT_VERSION = "2026-10-19";

export function consentText(base: string, country: string, controller: string): string {
  const body = (CONSENT[base] ?? CONSENT.en!).replaceAll("{controller}", controller);
  const addendum = COUNTRY_ADDENDA[country]?.[base] ?? "";
  return body + addendum;
}
